import React, { useState, useEffect } from 'react';
import '../AssessmentForm.css';

const reportID = '050B407B-7499-4182-8F80-4A341822C69C'; // Placeholder for report ID if needed

const WorkHistoryTab = ({ reportId, api, onSubmit, onSave , isPublished}) => {
  const [formData, setFormData] = useState({
    employerName: '',
    employerSite: '',
    currentlyAtWork: '',
    employmentStatus: '',
    jobTitle: '',
    workDuties: '',
    workRestrictions: ''
  });
  const [savingStates, setSavingStates] = useState({});
  const [saveTimeouts, setSaveTimeouts] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const fieldMapping = {
    employerName: 'EmployerName',
    employerSite: 'EmployerSite',
    currentlyAtWork: 'CurrentlyAtWork',
    employmentStatus: 'EmploymentStatus',
    jobTitle: 'JobTitle',
    workDuties: 'WorkDuties',
    workRestrictions: 'WorkRestrictions'
  };

  const currentlyAtWorkOptions = [
    { value: 'Y', label: 'Yes - Currently at work' },
    { value: 'N', label: 'No - Not currently at work' },
    { value: 'R', label: 'Restricted - Working with restrictions' },
    { value: '-', label: 'N/A - Not applicable' }
  ];

  // Load existing data from API
  useEffect(() => {
    const loadExistingData = async () => {
      try {
        setLoading(true);
        const effectiveReportId = reportId || reportID;
        const response = await api.webAPIFetch(`/api/PatientCaseReports/${effectiveReportId}/psych-triage-work-history`);

        if (response.ok) {
          const data = await response.json();

          setFormData({
            employerName: data.employerName || '',
            employerSite: data.employerSite || '',
            currentlyAtWork: data.currentlyAtWork || '',
            employmentStatus: data.employmentStatus || '',
            jobTitle: data.jobTitle || '',
            workDuties: data.workDuties || '',
            workRestrictions: data.workRestrictions || ''
          });

          // Mark all loaded fields as saved
          const savedStates = {};
          Object.keys(fieldMapping).forEach(field => {
            if (data[field]) {
              savedStates[field] = 'saved';
            }
          });
          setSavingStates(savedStates);
        }
      } catch (error) {
        console.error('Error loading work history:', error);
        setLoadError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadExistingData();
  }, [reportId]);

  // API call for saving individual field
  const saveField = async (fieldName, value) => {
    if (isPublished) return;
    console.log(`Saving Work History field: ${fieldName}, Value: ${value}`);

    const apiFieldName = fieldMapping[fieldName];
    const effectiveReportId = reportId || reportID;

    try {
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${effectiveReportId}/psych-triage-work-history/fields/${apiFieldName}`, {
        method: 'PATCH',
        body: JSON.stringify({
          value: value
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setSavingStates(prev => ({
        ...prev,
        [fieldName]: 'saved'
      }));

      if (onSave) {
        onSave();
      }

    } catch (error) {
      console.error(`Error saving field ${fieldName}:`, error);
      setSavingStates(prev => ({
        ...prev,
        [fieldName]: 'error'
      }));
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));

    // Clear any existing timeout for this field
    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    // Set saving state immediately
    setSavingStates(prev => ({
      ...prev,
      [fieldName]: 'saving'
    }));

    // Debounce the API call
    const timeoutId = setTimeout(() => {
      saveField(fieldName, value);
    }, 800); // Longer delay for text fields

    setSaveTimeouts(prev => ({
      ...prev,
      [fieldName]: timeoutId
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const result = {
      sectionType: 'WorkHistory',
      ...formData,
      completedAt: new Date().toISOString()
    };

    if (onSubmit) {
      await onSubmit(result);
    }
  };

  const getSaveStatus = (fieldName) => {
    const state = savingStates[fieldName];
    if (state === 'saving') {
      return <div className="save-status saving inline"><div className="spinner"></div></div>;
    }
    if (state === 'saved') {
      return <div className="save-status saved inline"><span className="checkmark">✓</span></div>;
    }
    if (state === 'error') {
      return <div className="save-status error inline"><span className="error-mark">⚠</span></div>;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="assessment-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading work history...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="assessment-container">
        <div className="error-state">
          <p>Error loading work history: {loadError}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-container">
      <fieldset disabled={isPublished} style={{ border: "none", padding: 0, margin: 0 }}>
      <div className="assessment-header">
        <h2>Work History</h2>
        <p>Please provide information about the patient's current employment and work situation.</p>
      </div>

      <form onSubmit={handleSubmit} className="assessment-form work-history-form">
        <div className="form-fields">
          <div className="form-field">
            <label htmlFor="employerName">
              Employer Name
              {getSaveStatus('employerName')}
            </label>
            <input
              type="text"
              id="employerName"
              value={formData.employerName}
              onChange={(e) => handleFieldChange('employerName', e.target.value)}
              placeholder="Enter employer name"
              className="form-input"
            />
          </div>

          <div className="form-field">
            <label htmlFor="employerSite">
              Employer Site/Location
              {getSaveStatus('employerSite')}
            </label>
            <input
              type="text"
              id="employerSite"
              value={formData.employerSite}
              onChange={(e) => handleFieldChange('employerSite', e.target.value)}
              placeholder="Enter site or location"
              className="form-input"
            />
          </div>

          <div className="form-field">
            <label htmlFor="currentlyAtWork">
              Currently at Work?
              {getSaveStatus('currentlyAtWork')}
            </label>
            <select
              id="currentlyAtWork"
              value={formData.currentlyAtWork}
              onChange={(e) => handleFieldChange('currentlyAtWork', e.target.value)}
              className="form-select"
            >
              <option value="">-- Select --</option>
              {currentlyAtWorkOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label htmlFor="employmentStatus">
              Employment Status
              {getSaveStatus('employmentStatus')}
            </label>
            <input
              type="text"
              id="employmentStatus"
              value={formData.employmentStatus}
              onChange={(e) => handleFieldChange('employmentStatus', e.target.value)}
              placeholder="e.g., Full-time, Part-time, Self-employed"
              className="form-input"
            />
          </div>

          <div className="form-field">
            <label htmlFor="jobTitle">
              Job Title/Occupation
              {getSaveStatus('jobTitle')}
            </label>
            <input
              type="text"
              id="jobTitle"
              value={formData.jobTitle}
              onChange={(e) => handleFieldChange('jobTitle', e.target.value)}
              placeholder="Enter job title"
              className="form-input"
            />
          </div>

          <div className="form-field">
            <label htmlFor="workDuties">
              Work Duties/Daily Responsibilities
              {getSaveStatus('workDuties')}
            </label>
            <textarea
              id="workDuties"
              value={formData.workDuties}
              onChange={(e) => handleFieldChange('workDuties', e.target.value)}
              placeholder="Describe the patient's work duties and daily responsibilities"
              className="form-textarea"
              rows="4"
            />
          </div>

          <div className="form-field">
            <label htmlFor="workRestrictions">
              Work Restrictions
              {getSaveStatus('workRestrictions')}
            </label>
            <textarea
              id="workRestrictions"
              value={formData.workRestrictions}
              onChange={(e) => handleFieldChange('workRestrictions', e.target.value)}
              placeholder="Describe any work restrictions in place"
              className="form-textarea"
              rows="3"
            />
          </div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="submit-button"
          >
            Save Work History
          </button>
        </div>
      </form>

      <style jsx>{`
        .work-history-form .form-fields {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .form-field {
          display: flex;
          flex-direction: column;
        }

        .form-field label {
          font-weight: 600;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .form-input,
        .form-select,
        .form-textarea {
          padding: 0.5rem;
          border: 1px solid #ccc;
          border-radius: 4px;
          font-size: 1rem;
          font-family: inherit;
        }

        .form-textarea {
          resize: vertical;
        }

        .form-input:focus,
        .form-select:focus,
        .form-textarea:focus {
          outline: none;
          border-color: #4a90e2;
          box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.1);
        }

        .save-status.inline {
          display: inline-flex;
          margin-left: 0.5rem;
        }

        .save-status.inline .spinner {
          width: 16px;
          height: 16px;
        }
      `}</style>
      </fieldset>

    </div>
  );
};

export default WorkHistoryTab;
